import * as THREE from "three";

/**
 * Blazor JS-interop bridge for the flat "creature" symbols (Pebble, Jelly, Dumpling, Bean, Sprout,
 * Hopper), rendered with Three.js.
 *
 * Each creature is flat 2D geometry on an orthographic camera (white patches are clipped to the
 * body with the stencil buffer). Animations: an idle squash-and-stretch hop, blinking, funny
 * expressions (happy ^^, wink, surprised, tongue-out), smiling when the cursor comes close, and
 * eyes + face that look at the mouse. The creatures are original designs in a flat illustration style.
 *
 * Speaking: while `speaking` is on the mouth talks on its own (syllable-like open/close with short
 * pauses between phrases). To follow a real voice instead, feed an audio level (0..1) every frame
 * with App.funnyShapesSetLevel(elementId, level) — no Blazor re-render needed.
 *
 * Exposed on window.App as:
 *  - App.funnyShapesSetLevel(elementId, level)  drive the talking mouth from a real audio level (0..1, <0 = off)
 *  - App.funnyShapesInit(elementId, options)     mount a Three.js canvas into the container div
 *  - App.funnyShapesUpdate(elementId, options)   update options live (rebuilds on creature / color change)
 *  - App.funnyShapesDispose(elementId)           stop the loop and free WebGL resources
 *
 * Called from FunnyShapesSymbol.razor.cs.
 */

export interface FunnyShapesInteropOptions {
    variant?: string;      // pebble | jelly | dumpling | bean | sprout | hopper
    bgColor?: string;      // hex
    bodyColor?: string;    // hex, "" = creature default
    patchColor?: string;   // hex, "" = default off-white
    speed?: number;        // animation speed multiplier
    bounce?: number;       // hop height multiplier
    squash?: number;       // squash & stretch multiplier
    scale?: number;        // zoom
    shadow?: number;       // ground shadow strength (0 = off)
    gaze?: number;         // how strongly the face follows the mouse (0 = off)
    playfulness?: number;  // how often funny expressions happen (0 = never)
    expression?: string;   // auto | neutral | happy | wink | surprise | tongue
    mode?: string;         // idle | listening | talking | thinking (talking = the mouth moves like speech)
    speaking?: boolean;    // legacy: same as mode "talking"
    voiceEnergy?: number;  // how wide the mouth opens while speaking (1 = default)
}

const INK = 0x10222b;
const PAPER = 0xfffdf7;
const TONGUE = 0xff7a93;

type Accessory = "none" | "leaf" | "antenna" | "sprig" | "buddy";
type Patch = Array<[number, number, number]>; // circles: x/a, y/b, r (world units)

interface Spec {
    key: string;
    body: number;
    a: number;
    b: number;
    n: number;
    taper?: number;
    flat?: number;
    patches: Patch[];
    feet: number[];
    eyeY: number;
    eyeDx: number;
    accessory: Accessory;
    accessoryColor: number;
    period: number;
    phase: number;
}

const SPECS: Spec[] = [
    {
        key: "pebble", body: 0xff8a7a, a: 0.62, b: 0.58, n: 2.3,
        patches: [[[0.62, 0.72, 0.3], [0.38, 0.55, 0.26], [0.8, 0.5, 0.22]], [[-0.78, -0.55, 0.26], [-0.55, -0.78, 0.2]]],
        feet: [-0.35, 0.35], eyeY: 0.05, eyeDx: 0.3, accessory: "sprig", accessoryColor: 0x5fe3b0, period: 2.4, phase: 0,
    },
    {
        key: "jelly", body: 0x5fe3b0, a: 0.46, b: 0.78, n: 4.5,
        patches: [[[-0.55, 0.85, 0.36], [-0.1, 0.9, 0.3], [-0.9, 0.55, 0.22]], [[0.95, -0.35, 0.26], [0.75, -0.6, 0.22]]],
        feet: [-0.55, 0, 0.55], eyeY: -0.25, eyeDx: 0.32, accessory: "antenna", accessoryColor: 0x2fc58f, period: 2.9, phase: 0.35,
    },
    {
        key: "dumpling", body: 0xffd23f, a: 0.8, b: 0.6, n: 3, flat: 0.62,
        patches: [[[0.15, 0.9, 0.4], [0.55, 0.75, 0.3], [-0.2, 0.75, 0.26]], [[-0.95, -0.2, 0.28]]],
        feet: [-0.5, 0, 0.5], eyeY: -0.05, eyeDx: 0.2, accessory: "antenna", accessoryColor: 0xffa51f, period: 2.2, phase: 0.6,
    },
    {
        key: "bean", body: 0xb6e84a, a: 0.92, b: 0.42, n: 4.2,
        patches: [[[0.9, -0.55, 0.28], [0.7, -0.8, 0.2]], [[-0.55, 0.7, 0.2], [-0.3, 0.75, 0.16]]],
        feet: [-0.62, -0.25, 0.25, 0.62], eyeY: 0.02, eyeDx: 0.14, accessory: "buddy", accessoryColor: 0xff8a7a, period: 3.1, phase: 0.15,
    },
    {
        key: "sprout", body: 0xb79cff, a: 0.62, b: 0.72, n: 2.4, taper: 0.5,
        patches: [[[-0.7, -0.55, 0.3], [-0.4, -0.7, 0.26]], [[0.75, -0.6, 0.24]]],
        feet: [-0.4, 0.4], eyeY: 0.1, eyeDx: 0.18, accessory: "leaf", accessoryColor: 0x5fe3b0, period: 2.6, phase: 0.8,
    },
    {
        key: "hopper", body: 0xff9f45, a: 0.7, b: 0.62, n: 3.5,
        patches: [[[-0.7, 0.8, 0.34], [-0.3, 0.9, 0.28], [-0.95, 0.5, 0.22]], [[0.8, -0.8, 0.3], [0.55, -0.95, 0.2]]],
        feet: [-0.4, 0.4], eyeY: 0.0, eyeDx: 0.26, accessory: "buddy", accessoryColor: 0x5fe3b0, period: 2.5, phase: 0.45,
    },
];

const FOOT_LIFT = 0.14;
const HOP_HEIGHT = 0.5;

// ---------- geometry helpers ----------

function superPoints(a: number, b: number, n: number, taper = 0, flat = 1, seg = 112): THREE.Vector2[] {
    const pts: THREE.Vector2[] = [];
    for (let i = 0; i < seg; i++) {
        const th = (i / seg) * Math.PI * 2;
        const c = Math.cos(th);
        const s = Math.sin(th);
        let x = a * Math.sign(c) * Math.pow(Math.abs(c), 2 / n);
        let y = b * Math.sign(s) * Math.pow(Math.abs(s), 2 / n);
        const t = (y / b + 1) / 2;
        x *= 1 - taper * t;
        y = Math.max(y, -b * flat);
        pts.push(new THREE.Vector2(x, y));
    }
    return pts;
}

function flatMat(color: number, stencil?: { ref: number; write: boolean }): THREE.MeshBasicMaterial {
    const m = new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false });
    if (stencil) {
        m.stencilWrite = true;
        m.stencilRef = stencil.ref;
        m.stencilFunc = stencil.write ? THREE.AlwaysStencilFunc : THREE.EqualStencilFunc;
        m.stencilZPass = stencil.write ? THREE.ReplaceStencilOp : THREE.KeepStencilOp;
    }
    return m;
}

function mesh(geo: THREE.BufferGeometry, mat: THREE.Material, order: number): THREE.Mesh {
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = order;
    return m;
}

function shapeGeo(pts: THREE.Vector2[]): THREE.ShapeGeometry {
    return new THREE.ShapeGeometry(new THREE.Shape(pts), 24);
}

function capsulePoints(w: number, h: number, seg = 14): THREE.Vector2[] {
    const r = w / 2;
    const pts: THREE.Vector2[] = [];
    for (let i = 0; i <= seg; i++) {
        const a = Math.PI * (i / seg);
        pts.push(new THREE.Vector2(Math.cos(a) * r, h - r + Math.sin(a) * r));
    }
    for (let i = 0; i <= seg; i++) {
        const a = Math.PI + Math.PI * (i / seg);
        pts.push(new THREE.Vector2(Math.cos(a) * r, r + Math.sin(a) * r));
    }
    return pts;
}

function circle(r: number, color: number, order: number, stencil?: { ref: number; write: boolean }): THREE.Mesh {
    return mesh(new THREE.CircleGeometry(r, 40), flatMat(color, stencil), order);
}

function darken(hex: number, k: number): number {
    const c = new THREE.Color(hex);
    c.multiplyScalar(k);
    return c.getHex();
}

function hexToInt(hex: string | undefined): number | undefined {
    if (!hex) return undefined;
    const h = hex.replace("#", "");
    if (h.length !== 6) return undefined;
    const v = parseInt(h, 16);
    return Number.isNaN(v) ? undefined : v;
}

function clamp(v: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, v)); }
function rand(min: number, max: number): number { return min + Math.random() * (max - min); }

// ---------- face ----------

interface Weights {
    blink: number;      // 0 open .. 1 closed
    happy: number;      // ^^ eyes
    wink: number;       // one eye closed into an arc
    winkSide: number;   // -1 | 1
    surprise: number;   // big eyes + "o" mouth
    open: number;       // open smiling mouth
    tongue: number;     // tongue sticking out
    talk: number;       // 0 not speaking .. 1 speaking
    mouthOpen: number;  // current speech mouth opening 0..1
    mouthWide: number;  // current speech mouth width factor
    think: number;      // 0 .. 1: squinting eyes, a small slanted "hmm" mouth
}

class Face {
    readonly group = new THREE.Group();
    private readonly eyes = new THREE.Group();
    private readonly dots: THREE.Mesh[] = [];
    private readonly arcs: THREE.Mesh[] = [];
    private readonly mouth = new THREE.Group();
    private readonly smile = new THREE.Group();
    private readonly openMouth = new THREE.Group();
    private readonly tongue: THREE.Mesh;
    private readonly oMouth: THREE.Mesh;
    private readonly ey: number;
    private readonly my: number;

    constructor(parent: THREE.Object3D, eyeY: number, eyeDx: number, order: number, eyeR: number, k: number) {
        this.ey = eyeY;
        this.my = eyeY - 0.02 * k;
        for (const side of [-1, 1]) {
            const dot = circle(eyeR, INK, order);
            dot.position.set(side * eyeDx, eyeY, 0);
            this.eyes.add(dot);
            this.dots.push(dot);

            const arc = mesh(new THREE.TorusGeometry(eyeR * 0.95, 0.017 * k, 6, 20, Math.PI), flatMat(INK), order);
            arc.position.set(side * eyeDx, eyeY - eyeR * 0.3, 0);
            arc.scale.setScalar(0.001);
            this.eyes.add(arc);
            this.arcs.push(arc);
        }
        this.group.add(this.eyes);

        const r = 0.075 * k;
        const smileArc = mesh(new THREE.TorusGeometry(r, 0.016 * k, 6, 24, Math.PI), flatMat(INK), order);
        smileArc.rotation.z = Math.PI;
        this.smile.add(smileArc);
        for (const s of [-1, 1]) {
            const cap = circle(0.016 * k, INK, order);
            cap.position.set(s * r, 0, 0);
            this.smile.add(cap);
        }

        const disc = mesh(new THREE.CircleGeometry(0.095 * k, 32, Math.PI, Math.PI), flatMat(INK), order);
        this.openMouth.add(disc);
        this.tongue = circle(0.04 * k, TONGUE, order + 1);
        this.tongue.position.set(0, -0.055 * k, 0);
        this.openMouth.add(this.tongue);
        this.oMouth = circle(0.04 * k, INK, order);

        this.mouth.position.set(0, this.my, 0);
        this.mouth.add(this.smile, this.openMouth, this.oMouth);
        this.group.add(this.mouth);
        parent.add(this.group);
    }

    update(w: Weights, gazeX: number, gazeY: number): void {
        // eyes look toward the cursor
        this.eyes.position.set(gazeX * 0.03, gazeY * 0.022, 0);
        this.mouth.position.set(gazeX * 0.012, this.my + gazeY * 0.008, 0);

        const open = 1 - w.blink;
        for (let i = 0; i < 2; i++) {
            const side = i === 0 ? -1 : 1;
            const winkHere = side === w.winkSide ? w.wink : 0;
            const arcW = clamp(Math.max(w.happy, winkHere), 0, 1);
            const dotW = 1 - arcW;
            const big = 1 + 0.4 * w.surprise;
            const squint = 1 - 0.3 * w.think;
            this.dots[i].scale.set(Math.max(0.001, dotW * big), Math.max(0.001, dotW * big * open * squint), 1);
            this.arcs[i].scale.setScalar(Math.max(0.001, arcW));
        }

        const talk = w.talk;
        const o = clamp(w.surprise, 0, 1) * (1 - talk);
        const baseOp = clamp(w.open, 0, 1) * (1 - o);
        const op = Math.max(baseOp, talk);
        const sm = Math.max(0.001, 1 - Math.max(op, o));
        // thinking squeezes the smile into a small slanted "hmm"
        this.smile.scale.set(sm * (1 + 0.15 * w.happy) * (1 - 0.4 * w.think), sm * (1 - 0.7 * w.think), 1);
        this.smile.rotation.z = Math.PI + 0.3 * w.think;
        // speech: the open mouth opens/closes (and widens/narrows) with the current syllable
        const sx = baseOp + (1.05 * w.mouthWide - baseOp) * talk;
        const sy = baseOp + (1.5 * Math.max(0.08, w.mouthOpen) - baseOp) * talk;
        this.openMouth.scale.set(Math.max(0.001, sx), Math.max(0.001, sy), 1);
        this.tongue.scale.setScalar(0.4 + 0.9 * w.tongue);
        this.tongue.position.y = -0.045 - 0.03 * w.tongue;
        this.oMouth.scale.setScalar(Math.max(0.001, o));
    }
}

// ---------- creature ----------

interface Creature {
    root: THREE.Group;
    squashGroup: THREE.Group;
    face: Face;
    faceGroup: THREE.Group;
    faceBaseY: number;
    shadow: THREE.Mesh;
    accessories: Array<{ obj: THREE.Object3D; base: number; dir: number }>;
    thoughts: Array<{ mesh: THREE.Mesh; x: number; y: number; r: number }>;   // the "..." that float up while thinking
    buddy?: { group: THREE.Group; face: Face; topY: number };
    spec: Spec;
    centerY: number;
    // expression state
    talkState: { sylT: number; phraseLeft: number; pauseLeft: number; tOpen: number; tWide: number };
    w: Weights;
    target: Weights;
    nextBlink: number;
    blinkT: number;
    kind: string;
    kindT: number;
    nextKind: number;
}

function buildCreature(spec: Spec, ref: number, patchColor: number): Creature {
    const root = new THREE.Group();
    const squashGroup = new THREE.Group();
    root.add(squashGroup);
    const orderBase = 0;

    const bodyPts = superPoints(spec.a, spec.b, spec.n, spec.taper ?? 0, spec.flat ?? 1);
    let minY = Infinity, maxY = -Infinity;
    for (const p of bodyPts) { minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
    const centerY = FOOT_LIFT - minY;

    const shadow = mesh(new THREE.CircleGeometry(1, 40), new THREE.MeshBasicMaterial({
        color: 0x000000, transparent: true, opacity: 0.2, depthTest: false, depthWrite: false,
    }), orderBase - 1);
    shadow.scale.set(spec.a * 0.95, 0.09, 1);
    shadow.position.y = -0.085;
    root.add(shadow);

    const footColor = darken(spec.body, 0.82);
    for (const fx of spec.feet) {
        const f = mesh(shapeGeo(capsulePoints(0.16, FOOT_LIFT + 0.12)), flatMat(footColor), orderBase);
        f.position.set(fx * spec.a, 0, 0);
        squashGroup.add(f);
    }

    const bodyGroup = new THREE.Group();
    bodyGroup.position.y = centerY;
    squashGroup.add(bodyGroup);
    bodyGroup.add(mesh(shapeGeo(bodyPts), flatMat(spec.body, { ref, write: true }), orderBase + 1));
    for (const cloud of spec.patches) {
        for (const [px, py, r] of cloud) {
            const c = circle(r, patchColor, orderBase + 2, { ref, write: false });
            c.position.set(px * spec.a, py * spec.b, 0);
            bodyGroup.add(c);
        }
    }

    const face = new Face(bodyGroup, spec.eyeY * spec.b, spec.eyeDx * spec.a, orderBase + 3, 0.05, 1);
    const faceBaseY = face.group.position.y;

    const accessories: Creature["accessories"] = [];
    let buddy: Creature["buddy"];
    const topY = maxY;
    const accColor = spec.accessoryColor;

    if (spec.accessory === "leaf") {
        for (const dir of [-1, 1]) {
            const leaf = new THREE.Group();
            const pts = superPoints(0.14, 0.26, 2, 0, 1, 48).map(p => new THREE.Vector2(p.x, p.y + 0.26));
            leaf.add(mesh(shapeGeo(pts), flatMat(dir < 0 ? accColor : darken(accColor, 0.85)), orderBase));
            leaf.position.set(dir * 0.06, topY - 0.06, 0);
            leaf.rotation.z = -dir * 0.75;
            bodyGroup.add(leaf);
            accessories.push({ obj: leaf, base: -dir * 0.75, dir });
        }
        const mid = new THREE.Group();
        mid.add(mesh(shapeGeo(superPoints(0.1, 0.22, 2, 0, 1, 48).map(p => new THREE.Vector2(p.x, p.y + 0.22))), flatMat(darken(accColor, 0.7)), orderBase));
        mid.position.set(0, topY - 0.06, 0);
        bodyGroup.add(mid);
        accessories.push({ obj: mid, base: 0, dir: 0.5 });
    } else if (spec.accessory === "antenna") {
        for (const dir of [-1, 1]) {
            const ant = new THREE.Group();
            ant.add(mesh(shapeGeo(capsulePoints(0.04, 0.24)), flatMat(accColor), orderBase));
            ant.position.set(dir * 0.18 * spec.a, topY - 0.05, 0);
            ant.rotation.z = -dir * 0.35;
            bodyGroup.add(ant);
            accessories.push({ obj: ant, base: -dir * 0.35, dir });
        }
    } else if (spec.accessory === "sprig") {
        const sp = new THREE.Group();
        sp.add(mesh(shapeGeo(capsulePoints(0.045, 0.26)), flatMat(darken(accColor, 0.8)), orderBase));
        const bulb = circle(0.085, accColor, orderBase);
        bulb.position.set(0, 0.28, 0);
        sp.add(bulb);
        sp.position.set(-0.1 * spec.a, topY - 0.05, 0);
        sp.rotation.z = 0.3;
        bodyGroup.add(sp);
        accessories.push({ obj: sp, base: 0.3, dir: 1 });
    } else if (spec.accessory === "buddy") {
        const g = new THREE.Group();
        const bpts = superPoints(0.2, 0.15, 3);
        let bmin = Infinity;
        for (const p of bpts) bmin = Math.min(bmin, p.y);
        for (const fx of [-0.4, 0.4]) {
            const f = mesh(shapeGeo(capsulePoints(0.07, 0.1)), flatMat(darken(accColor, 0.82)), orderBase + 4);
            f.position.set(fx * 0.2, 0, 0);
            g.add(f);
        }
        const inner = new THREE.Group();
        inner.position.y = 0.06 - bmin;
        g.add(inner);
        inner.add(mesh(shapeGeo(bpts), flatMat(accColor, { ref: ref + 20, write: true }), orderBase + 5));
        const p = circle(0.07, patchColor, orderBase + 6, { ref: ref + 20, write: false });
        p.position.set(0.12, 0.1, 0);
        inner.add(p);
        const bface = new Face(inner, 0.15 * 0.05, 0.2 * 0.38, orderBase + 7, 0.03, 0.55);
        g.position.set(spec.a * 0.2, topY, 0);
        bodyGroup.add(g);
        buddy = { group: g, face: bface, topY };
    }

    // thought bubbles: three small circles climbing diagonally away from the head
    const thoughts: Creature["thoughts"] = [];
    for (let i = 0; i < 3; i++) {
        const r = 0.07 + 0.035 * i;
        const bubble = circle(1, PAPER, orderBase + 8);
        bubble.scale.setScalar(0.001);
        const x = spec.a * (0.5 + 0.25 * i);
        const y = topY + 0.16 + 0.18 * i;
        bubble.position.set(x, y, 0);
        bodyGroup.add(bubble);
        thoughts.push({ mesh: bubble, x, y, r });
    }

    const neutral = (): Weights => ({ blink: 0, happy: 0, wink: 0, winkSide: 1, surprise: 0, open: 0, tongue: 0, talk: 0, mouthOpen: 0, mouthWide: 1, think: 0 });
    return {
        root, squashGroup, face, faceGroup: face.group, faceBaseY, shadow, accessories, thoughts, buddy, spec, centerY,
        w: neutral(), target: neutral(), talkState: { sylT: 0, phraseLeft: 1.2, pauseLeft: 0, tOpen: 0, tWide: 1 },
        nextBlink: rand(1, 3), blinkT: 0, kind: "neutral", kindT: 0, nextKind: rand(1.5, 4),
    };
}

// ---------- animation ----------

interface BounceState { y: number; sy: number; sx: number }

function smooth(k: number): number { return k * k * (3 - 2 * k); }

function bounceState(uIn: number, bounce: number, squash: number): BounceState {
    const u = ((uIn % 1) + 1) % 1;
    let y = 0;
    let sy = 1;
    if (u < 0.14) {
        sy = 1 - 0.14 * squash * smooth(u / 0.14);
    } else if (u < 0.5) {
        const k = (u - 0.14) / 0.36;
        y = HOP_HEIGHT * bounce * 4 * k * (1 - k);
        sy = 1 + squash * (-0.14 * Math.exp(-k * 12) + 0.13 * (1 - Math.sin(k * Math.PI)));
    } else if (u < 0.62) {
        const k = smooth((u - 0.5) / 0.12);
        sy = (1 + 0.13 * squash) * (1 - k) + (1 - 0.18 * squash) * k;
    } else {
        const k = (u - 0.62) / 0.38;
        sy = 1 - 0.18 * squash * Math.exp(-5 * k) * Math.cos(k * 13);
    }
    return { y, sy, sx: Math.pow(sy, -0.7) };
}

// ---------- mount ----------

interface FunnyMount {
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera;
    container: HTMLElement;
    observer: ResizeObserver;
    raf: number;
    options: FunnyShapesInteropOptions;
    creature?: Creature;
    bounds: { w: number; h: number; cy: number };
    time: number;
    lastFrame: number;
    pointer: { x: number; y: number; active: boolean };
    gaze: { x: number; y: number };
    speechLevel: number;   // <0 = procedural speech, otherwise a real audio level 0..1
    mw: { listen: number; talk: number; think: number };   // eased mode weights (0..1 each)
    onMove: (e: PointerEvent) => void;
    onLeave: () => void;
}

const mounts = new Map<string, FunnyMount>();

function disposeObject(o: THREE.Object3D): void {
    o.traverse(c => {
        const m = c as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
        const mat = m.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach(x => x.dispose());
        else if (mat) mat.dispose();
    });
}

function rebuild(m: FunnyMount): void {
    if (m.creature) { m.scene.remove(m.creature.root); disposeObject(m.creature.root); m.creature = undefined; }

    const base = SPECS.find(s => s.key === (m.options.variant ?? "pebble")) ?? SPECS[0];
    const spec: Spec = { ...base, body: hexToInt(m.options.bodyColor) ?? base.body };
    const c = buildCreature(spec, 1, hexToInt(m.options.patchColor) ?? PAPER);
    c.root.position.set(0, -0.75, 0);
    m.scene.add(c.root);
    m.creature = c;
    m.bounds = { w: 3.4, h: 3.3, cy: 0.05 };
    fit(m);
}

function fit(m: FunnyMount): void {
    const w = Math.max(1, m.container.clientWidth);
    const h = Math.max(1, m.container.clientHeight);
    m.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    m.renderer.setSize(w, h, false);
    const canvas = m.renderer.domElement;
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    const zoom = m.options.scale ?? 1;
    const aspect = w / h;
    let vw = m.bounds.w / zoom;
    let vh = m.bounds.h / zoom;
    if (vw / vh < aspect) vw = vh * aspect; else vh = vw / aspect;
    m.camera.left = -vw / 2;
    m.camera.right = vw / 2;
    m.camera.top = m.bounds.cy + vh / 2;
    m.camera.bottom = m.bounds.cy - vh / 2;
    m.camera.updateProjectionMatrix();
}

/** Pointer position (client px) -> world coordinates of the orthographic camera. */
function pointerWorld(m: FunnyMount): THREE.Vector2 {
    const rect = m.renderer.domElement.getBoundingClientRect();
    const nx = (m.pointer.x - rect.left) / Math.max(1, rect.width);
    const ny = (m.pointer.y - rect.top) / Math.max(1, rect.height);
    return new THREE.Vector2(
        m.camera.left + nx * (m.camera.right - m.camera.left),
        m.camera.top - ny * (m.camera.top - m.camera.bottom),
    );
}

function setKind(c: Creature, kind: string): void {
    c.kind = kind;
    const t = c.target;
    t.happy = 0; t.wink = 0; t.surprise = 0; t.open = 0; t.tongue = 0;
    switch (kind) {
        case "happy": t.happy = 1; t.open = 0.85; break;
        case "wink": t.wink = 1; t.winkSide = Math.random() < 0.5 ? -1 : 1; break;
        case "surprise": t.surprise = 1; break;
        case "tongue": t.wink = 1; t.winkSide = Math.random() < 0.5 ? -1 : 1; t.open = 1; t.tongue = 1; break;
        default: break;
    }
}

function modeOf(o: FunnyShapesInteropOptions): string {
    return o.mode ?? (o.speaking ? "talking" : "idle");
}

function updateFace(m: FunnyMount, c: Creature, dt: number, st: BounceState): void {
    const o = m.options;
    const mode = modeOf(o);
    const forced = o.expression ?? "auto";
    const playful = o.playfulness ?? 1;

    // blinking
    c.nextBlink -= dt;
    if (c.nextBlink <= 0 && c.blinkT <= 0) { c.blinkT = 0.16; c.nextBlink = rand(1.8, 4.5); }
    let blink = 0;
    if (c.blinkT > 0) {
        c.blinkT -= dt;
        const k = 1 - Math.max(0, c.blinkT) / 0.16;
        blink = Math.sin(k * Math.PI);
    }

    // funny expressions
    if (forced !== "auto") {
        if (c.kind !== forced) setKind(c, forced);
    } else {
        c.nextKind -= dt;
        if (c.kind !== "neutral") {
            c.kindT -= dt;
            if (c.kindT <= 0) { setKind(c, "neutral"); c.nextKind = rand(1.8, 4.5) / Math.max(0.1, playful); }
        } else if (c.nextKind <= 0 && playful > 0.01 && mode === "idle") {
            const kinds = ["happy", "wink", "surprise", "tongue"];
            setKind(c, kinds[Math.floor(Math.random() * kinds.length)]);
            c.kindT = rand(0.9, 1.6);
        }
    }

    // smile when the cursor is near
    const t = { ...c.target };
    if (forced === "auto" && c.kind === "neutral" && m.pointer.active) {
        const p = pointerWorld(m);
        const d = p.distanceTo(new THREE.Vector2(0, c.root.position.y + c.centerY));
        const near = clamp(1 - d / 1.5, 0, 1);
        t.happy = Math.max(t.happy, near * 0.85);
        t.open = Math.max(t.open, near * 0.7);
    }

    // listening: eyes a little wider, as if leaning in
    t.surprise = Math.max(t.surprise, 0.3 * m.mw.listen);

    const k = 1 - Math.exp(-dt * 14);
    const w = c.w;

    // speech
    const speaking = mode === "talking";
    const energy = clamp(o.voiceEnergy ?? 1, 0.2, 2);
    w.talk += ((speaking ? 1 : 0) - w.talk) * (1 - Math.exp(-dt * 10));
    if (speaking) {
        const ts = c.talkState;
        if (m.speechLevel >= 0) {
            ts.tOpen = clamp(m.speechLevel * 1.7 * energy, 0, 1);
            ts.tWide = 1.1 - 0.4 * ts.tOpen;
        } else if (ts.pauseLeft > 0) {
            ts.pauseLeft -= dt;
            ts.tOpen = 0;
            if (ts.pauseLeft <= 0) ts.phraseLeft = rand(1.2, 2.8);
        } else {
            ts.phraseLeft -= dt;
            ts.sylT -= dt;
            if (ts.sylT <= 0) {
                ts.tOpen = clamp(rand(0.3, 1) * energy, 0, 1);
                ts.tWide = rand(0.7, 1.2);
                if (Math.random() < 0.18) ts.tOpen *= 0.25;     // a consonant: mouth nearly shut
                ts.sylT = rand(0.09, 0.2);
            }
            if (ts.phraseLeft <= 0) ts.pauseLeft = rand(0.3, 0.8);
        }
        w.mouthOpen += (ts.tOpen - w.mouthOpen) * (1 - Math.exp(-dt * 28));
        w.mouthWide += (ts.tWide - w.mouthWide) * (1 - Math.exp(-dt * 20));
    } else {
        w.mouthOpen += (0 - w.mouthOpen) * (1 - Math.exp(-dt * 12));
    }

    w.happy += (t.happy - w.happy) * k;
    w.wink += (t.wink - w.wink) * k;
    w.surprise += (t.surprise - w.surprise) * k;
    w.open += (t.open - w.open) * k;
    w.tongue += (t.tongue - w.tongue) * k;
    w.winkSide = t.winkSide;
    w.think += (m.mw.think - w.think) * k;
    w.blink = blink;

    // gaze: toward the pointer, or a lazy wander when the pointer is away
    const strength = o.gaze ?? 1;
    let gx: number, gy: number;
    if (m.pointer.active) {
        const p = pointerWorld(m);
        const dx = p.x - 0;
        const dy = p.y - (c.root.position.y + c.centerY + st.y);
        const len = Math.hypot(dx, dy);
        const f = len > 1e-4 ? Math.min(1, len / 1.2) / len : 0;
        gx = dx * f;
        gy = dy * f;
    } else {
        const wt = performance.now() / 1000;
        gx = Math.sin(wt * 0.6) * 0.5;
        gy = Math.sin(wt * 0.43 + 1.3) * 0.25;
    }
    gx *= strength * (1 + 0.5 * m.mw.listen);
    gy *= strength * (1 + 0.5 * m.mw.listen);
    if (!m.pointer.active) { gx *= 1 - m.mw.listen; gy *= 1 - m.mw.listen; }   // listening: look straight at you
    // thinking: eyes drift up and to the side, like searching for the answer
    const wt = performance.now() / 1000;
    gx += (-0.9 + 0.25 * Math.sin(wt * 0.7) - gx) * m.mw.think;
    gy += (0.85 + 0.12 * Math.sin(wt * 1.1) - gy) * m.mw.think;
    const gk = 1 - Math.exp(-dt * 9);
    m.gaze.x += (gx - m.gaze.x) * gk;
    m.gaze.y += (gy - m.gaze.y) * gk;

    c.face.update(w, m.gaze.x, m.gaze.y);
    if (c.buddy) {
        const bw: Weights = { ...w, wink: 0, open: 0, tongue: 0, surprise: w.surprise * 0.6 };
        c.buddy.face.update({ ...bw, talk: 0 }, m.gaze.x, m.gaze.y);
    }
}

function animate(m: FunnyMount, dt: number, realDt: number): void {
    const c = m.creature;
    if (!c) return;
    const o = m.options;
    const mw = m.mw;
    // listening holds still and leans in, thinking slows right down, talking hops a bit more
    const bounce = (o.bounce ?? 1) * (1 - 0.7 * mw.listen) * (1 - 0.6 * mw.think) * (1 + 0.25 * mw.talk);
    const squash = o.squash ?? 1;
    const shadow = o.shadow ?? 1;
    const t = m.time;
    const s = c.spec;

    const u = t / s.period + s.phase;
    const st = bounceState(u, bounce, squash);
    const lag = bounceState(u - 0.05, bounce, squash);
    const lag2 = bounceState(u - 0.1, bounce, squash);

    c.squashGroup.position.y = st.y;
    c.squashGroup.scale.set(st.sx, st.sy, 1);
    // lean toward the cursor; listening tilts the head, thinking cocks it the other way and sways, talking nods
    c.squashGroup.rotation.z = -m.gaze.x * 0.035
        + mw.listen * (0.07 + Math.sin(t * 0.9) * 0.04)
        - mw.think * (0.12 + Math.sin(t * 1.3) * 0.05)
        + mw.talk * Math.sin(t * 5) * 0.02;

    updateFace(m, c, realDt, st);   // face keeps its own clock so it stays alive at speed 0

    // talking: the body pulses a little with every syllable and the head sways
    const talkAmt = c.w.talk * c.w.mouthOpen;
    c.squashGroup.scale.y *= 1 + 0.03 * talkAmt;
    c.squashGroup.scale.x *= 1 - 0.015 * talkAmt;

    // the whole face slides toward the cursor a little, with squash lag on top
    c.faceGroup.position.x = m.gaze.x * s.a * 0.14 + Math.sin(performance.now() / 1000 * 6) * 0.012 * c.w.talk;
    c.faceGroup.position.y = c.faceBaseY + (lag.sy - st.sy) * s.b * 0.7 + m.gaze.y * s.b * 0.07 + 0.015 * talkAmt;

    // accessories: they perk up while listening, and fidget while thinking (leaves and antennae the most)
    const fidget = s.accessory === "leaf" ? 0.25 : s.accessory === "antenna" ? 0.2 : 0.1;
    for (const a of c.accessories) {
        a.obj.rotation.z = a.base * (1 - 0.6 * mw.listen)
            + Math.sin(t * 2.4 + a.dir) * 0.07 * a.dir
            + (lag2.sy - st.sy) * 1.6 * a.dir
            + Math.sin(t * 7 + a.dir * 2) * fidget * mw.think * a.dir
            + Math.sin(t * 9) * 0.05 * mw.talk * a.dir;
    }

    // thought bubbles: pop in one after another, hold, then fade together
    const cycle = (t * 1.1) % 4;
    c.thoughts.forEach((b, i) => {
        const pop = smooth(clamp(cycle - i * 0.6, 0, 1));
        const fade = cycle > 3.2 ? clamp((4 - cycle) / 0.8, 0, 1) : 1;
        const k = mw.think * pop * fade;
        b.mesh.scale.setScalar(Math.max(0.001, b.r * k));
        b.mesh.position.set(b.x, b.y + Math.sin(t * 3 + i) * 0.015, 0);
    });
    if (c.buddy) {
        const bu = bounceState(u + 0.2, bounce, squash);
        c.buddy.group.position.y = c.buddy.topY - 0.02 + bu.y * 0.2 * (1 + 1.5 * mw.talk);
        c.buddy.group.scale.set(bu.sx, bu.sy, 1);
    }

    const hNorm = Math.min(1, st.y / (HOP_HEIGHT * Math.max(bounce, 0.01)));
    c.shadow.visible = shadow > 0.001;
    (c.shadow.material as THREE.MeshBasicMaterial).opacity = 0.2 * shadow * (1 - 0.6 * hNorm);
    const sw = 1 - 0.3 * hNorm;
    c.shadow.scale.set(s.a * 0.95 * sw * (0.9 + 0.1 * st.sx), 0.09 * sw, 1);
}

export function funnyShapesInit(elementId: string, options: FunnyShapesInteropOptions): void {
    funnyShapesDispose(elementId);

    const container = document.getElementById(elementId);
    if (!container) return;

    try {
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, stencil: true });
        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -10, 10);
        container.appendChild(renderer.domElement);

        const mount: FunnyMount = {
            renderer, scene, camera, container,
            observer: undefined as unknown as ResizeObserver,
            raf: 0,
            options: { ...options },
            bounds: { w: 4, h: 4, cy: 0 },
            time: 0,
            lastFrame: performance.now(),
            pointer: { x: 0, y: 0, active: false },
            gaze: { x: 0, y: 0 },
            speechLevel: -1,
            mw: { listen: 0, talk: 0, think: 0 },
            onMove: () => undefined,
            onLeave: () => undefined,
        };
        mount.onMove = (e: PointerEvent): void => {
            mount.pointer.x = e.clientX;
            mount.pointer.y = e.clientY;
            mount.pointer.active = true;
        };
        mount.onLeave = (): void => { mount.pointer.active = false; };
        window.addEventListener("pointermove", mount.onMove, { passive: true });
        document.documentElement.addEventListener("mouseleave", mount.onLeave);

        renderer.setClearColor(new THREE.Color(options.bgColor ?? "#0b2a2e"), 1);
        mount.observer = new ResizeObserver(() => fit(mount));
        mount.observer.observe(container);
        mounts.set(elementId, mount);
        rebuild(mount);

        const loop = (now: number): void => {
            mount.raf = requestAnimationFrame(loop);
            if (document.hidden) { mount.lastFrame = now; return; }
            const realDt = Math.min(0.05, (now - mount.lastFrame) / 1000);
            const mode = modeOf(mount.options);
            const mk = 1 - Math.exp(-realDt * 3.5);
            const mw = mount.mw;
            mw.listen += ((mode === "listening" ? 1 : 0) - mw.listen) * mk;
            mw.talk += ((mode === "talking" ? 1 : 0) - mw.talk) * mk;
            mw.think += ((mode === "thinking" ? 1 : 0) - mw.think) * mk;
            const dt = realDt * (mount.options.speed ?? 1) * (1 - 0.35 * mw.think + 0.2 * mw.talk);
            mount.lastFrame = now;
            mount.time += dt;
            animate(mount, dt, realDt);
            renderer.render(scene, camera);
        };
        mount.raf = requestAnimationFrame(loop);
    } catch (error) {
        console.error("[FunnyShapes] Failed to initialize:", error);
    }
}

export function funnyShapesUpdate(elementId: string, options: FunnyShapesInteropOptions): void {
    const mount = mounts.get(elementId);
    if (!mount) return;
    try {
        const prev = mount.options;
        const rebuildNeeded = (options.variant ?? "pebble") !== (prev.variant ?? "pebble")
            || (options.bodyColor ?? "") !== (prev.bodyColor ?? "")
            || (options.patchColor ?? "") !== (prev.patchColor ?? "");
        mount.options = { ...options };
        mount.renderer.setClearColor(new THREE.Color(options.bgColor ?? "#0b2a2e"), 1);
        if (rebuildNeeded) rebuild(mount); else fit(mount);
    } catch (error) {
        console.error("[FunnyShapes] Failed to update:", error);
    }
}

export function funnyShapesSetLevel(elementId: string, level: number): void {
    const mount = mounts.get(elementId);
    if (mount) mount.speechLevel = level;
}

export function funnyShapesDispose(elementId: string): void {
    const mount = mounts.get(elementId);
    if (!mount) return;
    try {
        cancelAnimationFrame(mount.raf);
        mount.observer.disconnect();
        window.removeEventListener("pointermove", mount.onMove);
        document.documentElement.removeEventListener("mouseleave", mount.onLeave);
        if (mount.creature) disposeObject(mount.creature.root);
        mount.renderer.dispose();
        mount.renderer.forceContextLoss();
        mount.renderer.domElement.remove();
    } catch (error) {
        console.error("[FunnyShapes] Failed to dispose:", error);
    }
    mounts.delete(elementId);
}

// app.ts (which creates window.App) may run before or after this module inside the same esbuild
// bundle depending on the order of the entry file's imports. Attach immediately when possible and
// always re-attach on the next tick, once the whole bundle has finished executing.
const attachToApp = (): void => {
    const w = window as any;
    const app = w.App ?? (w.App = {});
    app.funnyShapesInit = funnyShapesInit;
    app.funnyShapesUpdate = funnyShapesUpdate;
    app.funnyShapesSetLevel = funnyShapesSetLevel;
    app.funnyShapesDispose = funnyShapesDispose;
};

attachToApp();
setTimeout(attachToApp, 0);

export {};
